import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import CustomTooltip from '../../CustomTooltip';
import {
    colors,
    lineGraphDotSize,
} from '../../../../shared/constants/constants';
import {
    getXAxisLabel,
    getYAxisLabel,
    getChartMargin,
    getXAxisDataKey,
    getXAxisTickFormatter,
    getYAxisDataKey,
    getYAxisTickFormatter,
} from '../../../dashboardUtils';

function GaugeLineGraph({
    chartProperties,
    dataFormatter,
    dataLabels,
    dataPoints,
    firstGroupKey,
    legendFormatter,
    thresholds,
    groupAxisFormatter,
    xAxisLabel,
    yAxisLabelLeft,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        colorPalette = colors,
        lineChartProperties,
        lineProperties,
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend,
        suffix,
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const isVertical = lineChartProperties.layout === 'vertical';
    const xAxisTickFormatter = getXAxisTickFormatter(
        isVertical,
        groupAxisFormatter,
        dataFormatter,
        timeFormat,
        suffix
    );
    const yAxisTickFormatter = getYAxisTickFormatter(
        isVertical,
        groupAxisFormatter,
        dataFormatter,
        timeFormat,
        suffix
    );

    const chartMargin = getChartMargin(
        dataPoints,
        firstGroupKey,
        suffix,
        thresholds,
        timeFormat,
        yAxisTickFormatter,
        yAxisLabelLeft,
        isVertical
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <LineChart {...lineChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    {...xAxisProperties}
                    dataKey={getXAxisDataKey(isVertical, firstGroupKey)}
                    tickFormatter={xAxisTickFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    dataKey={getYAxisDataKey(isVertical, firstGroupKey)}
                    tickFormatter={yAxisTickFormatter}
                    label={getYAxisLabel(yAxisLabelLeft)}
                    width={chartMargin.left}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={
                                isVertical
                                    ? yAxisTickFormatter
                                    : xAxisTickFormatter
                            }
                            axisKey={firstGroupKey}
                            dataFormat={timeFormat}
                            dataFormatter={dataFormatter}
                            legendFormatter={legendFormatter}
                            suffix={suffix}
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {dataLabels.map((dataLabel, index) => (
                    <Line
                        {...lineProperties}
                        key={dataLabel}
                        dataKey={dataLabel}
                        stroke={colorPalette[index % colorPalette.length]}
                        activeDot={{
                            r: lineGraphDotSize,
                            onMouseOver: () => setActiveIndex(index),
                            onMouseOut: () => setActiveIndex(-1),
                            onFocus: () => setActiveIndex(index),
                            onBlur: () => setActiveIndex(-1),
                        }}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        x={isVertical ? threshold.value : null}
                        y={isVertical ? null : threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

GaugeLineGraph.propTypes = {
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataFormatter: PropTypes.func.isRequired,
    dataLabels: PropTypes.instanceOf(Array),
    dataPoints: PropTypes.instanceOf(Array),
    firstGroupKey: PropTypes.string.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
    groupAxisFormatter: PropTypes.func.isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
};

GaugeLineGraph.defaultProps = {
    dataLabels: [],
    dataPoints: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
};

export default React.memo(
    GaugeLineGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
